import React, { useRef, useState, useEffect } from "react";
import ButtonOnFieldPlayerView from "shared/components/buttons/ButtonOnFieldPlayerView.js";
import PopupSelectPlayer from "shared/components/popups/PopupSelectPlayer.js";
import { formations } from "shared/utils/formation.js";
import MiscFootballFieldPlayer from "shared/components/misc/MiscFootballFieldPlayer";
import MiscOverall from "shared/components/misc/MiscOverall";
import { getCalculatedOverall } from "shared/utils/player.js";

interface MiscFootballFieldForSquadProps {
  team: any;
  formation: string;
  saveTeamMember: (id: string, position: number | null) => void;
}

const MiscFootballFieldForSquad: React.FC<MiscFootballFieldForSquadProps> = ({ team, formation, saveTeamMember }) => {
  const fieldRef = useRef(null);
  const [parentSize, setParentSize] = useState({ width: 0, height: 0 });

  const [onFieldPlayerView, setOnFieldPlayerView] = useState(null);

  // ResizeObserver to update parent size dynamically
  useEffect(() => {
    if (!fieldRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setParentSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(fieldRef.current);
    return () => observer.disconnect();
  }, []);

  const getTeamMemberInPosition = (position: number) =>
    team.teamMembers?.filter((tm) => tm.position === position).pop() || null;

  return (
    <div className="position-relative football-field football-field--responsive-players w-100 h-100" ref={fieldRef}>
      <div className="football-field-lines">
        <div className="halfway-line" />
        <div className="center-circle" />
        <div className="goal-box" />
      </div>

      <div className="position-absolute" style={{ top: "1px", right: "1px" }}>
        <ButtonOnFieldPlayerView selectedView={onFieldPlayerView} onChange={(t) => setOnFieldPlayerView(t)} />
      </div>

      {formation &&
        Object.keys(formations[formation]).map((p) => {
          const position = parseInt(p);
          const tm = getTeamMemberInPosition(position);

          return (
            <div
              key={p}
              style={{
                position: "absolute",
                top: formations[formation][p].y + "%",
                left: formations[formation][p].x + "%",
                transform: "translate(-50%,-50%)",
              }}
            >
              {onFieldPlayerView === "depth" ? (
                <div
                  className="d-flex flex-column"
                  style={{
                    fontSize: parentSize.width < 300 ? 10 : 12,
                    lineHeight: 1.0,
                    backgroundColor: "rgba(33, 37, 41, .9)",
                    color: "#fff",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "80px",
                    padding: "4px 6px",
                    width: parentSize.width * 0.22,
                    minWidth: 65,
                    zIndex: 3,
                  }}
                >
                  {(() => {
                    const bestTwo = team.teamMembers
                      .filter((tm) => (p === "1" ? tm.player.positions[0] === "GK" : true))
                      .map((tm) => {
                        const actualPos = formations[team.formation][p].position.toString();
                        const calc = getCalculatedOverall(tm.player, actualPos);
                        return {
                          player: tm.player,
                          actualPos,
                          calc,
                          diff: calc - tm.player.overall,
                        };
                      })
                      .sort((a, b) => b.calc - a.calc)
                      .slice(0, 4);

                    return bestTwo.map((pl, idx) => (
                      <div key={idx} className="d-flex flex-column w-100">
                        <div key={idx} className="d-flex flex-row">
                          <div className="d-flex flex-fill">{pl.player.lastName.substring(0, 3)}.</div>
                          <div className="d-flex" style={{ width: 35 }}>
                            <MiscOverall player={pl.player} actualPosition={pl.actualPos} calculatedOvr={true} />
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              ) : tm ? (
                <MiscFootballFieldPlayer
                  teamMember={tm}
                  formation={formation}
                  onFieldPlayerView={onFieldPlayerView}
                  onClick={() => saveTeamMember(tm.id, null)}
                />
              ) : (
                <PopupSelectPlayer
                  trigger={
                    <button className="btn btn-info btn-small text-white">
                      <i className="bi bi-person-fill-add me-1" />
                      {formations[formation][p].position}
                    </button>
                  }
                  teamMembers={team.teamMembers}
                  actualPosition={formations[formation][p].position.toString()}
                  onConfirm={(m) => saveTeamMember(m.id, position)}
                />
              )}
            </div>
          );
        })}
    </div>
  );
};

export default MiscFootballFieldForSquad;
